import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { GastoForm, GastoItemForm } from "../types";

interface GastosStepProps {
  nuevoGasto: GastoForm;
  gastos: GastoForm[];
  totalGastos: number;
  onNuevoGastoChange: (campo: keyof GastoForm, valor: string | number) => void;
  onAgregarGasto: () => void;
  onEliminarGasto: (id: number) => void;
  onAgregarItem: () => void;
  onEliminarItem: (index: number) => void;
  onActualizarItem: (index: number, campo: keyof GastoItemForm, valor: string | number) => void;
}

const GastosStep = ({
  nuevoGasto,
  gastos,
  totalGastos,
  onNuevoGastoChange,
  onAgregarGasto,
  onEliminarGasto,
  onAgregarItem,
  onEliminarItem,
  onActualizarItem,
}: GastosStepProps) => {
  const [modalAbierto, setModalAbierto] = useState(false);
  const toggleModal = () => setModalAbierto((prev) => !prev);

  const puedeGuardar = useMemo(
    () => nuevoGasto.descripcion.trim().length > 0 && nuevoGasto.proveedor.trim().length > 0,
    [nuevoGasto.descripcion, nuevoGasto.proveedor],
  );

  const manejarSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!puedeGuardar) {
      return;
    }
    onAgregarGasto();
    setModalAbierto(false);
  };

  return (
    <>
      <Card>
        <CardBody className="pt-0">
          <div className="table-responsive">
            <Table className="align-middle">
              <thead className="table-light">
                <tr>
                  <th>Descripción</th>
                  <th>Proveedor</th>
                  <th>Comprobante</th>
                  <th>Monto</th>
                  <th className="text-end">
                    <Button color="primary" size="sm" onClick={toggleModal}>
                      + Agregar gasto
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {gastos.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">
                      Todavía no se registraron gastos.
                    </td>
                  </tr>
                ) : (
                  gastos.map((gasto) => (
                    <tr key={gasto.id}>
                      <td>{gasto.descripcion}</td>
                      <td>{gasto.proveedor || "-"}</td>
                      <td>
                        {gasto.numero || "-"}
                        {gasto.fecha && (
                          <small className="text-muted d-block">{new Date(gasto.fecha).toLocaleDateString("es-PY")}</small>
                        )}
                      </td>
                      <td>Gs. {gasto.monto.toLocaleString("es-PY")}</td>
                      <td className="text-end">
                        <Button color="link" className="text-danger" onClick={() => onEliminarGasto(gasto.id)}>
                          Quitar
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={3}>Total</th>
                  <th>Gs. {totalGastos.toLocaleString("es-PY")}</th>
                  <th></th>
                </tr>
              </tfoot>
            </Table>
          </div>
        </CardBody>
      </Card>

      <Modal isOpen={modalAbierto} toggle={toggleModal} size="xl" centered backdrop="static">
        <Form onSubmit={manejarSubmit}>
          <ModalHeader toggle={toggleModal}>
            Datos del gasto
            <p className="text-muted mb-0 fs-6">Ingresá los datos principales del comprobante y del proveedor.</p>
          </ModalHeader>
          <ModalBody>
            <Row className="g-3">
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="gastoTipo">
                    Tipo de gasto
                  </Label>
                  <Input
                    id="gastoTipo"
                    type="select"
                    value={nuevoGasto.tipo}
                    onChange={(event) => onNuevoGastoChange("tipo", event.target.value)}
                  >
                    <option value="operativo">Operativo</option>
                    <option value="administrativo">Administrativo</option>
                    <option value="financiero">Financiero</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="gastoCompraTipo">
                    Condición
                  </Label>
                  <Input
                    id="gastoCompraTipo"
                    type="select"
                    value={nuevoGasto.compraTipo}
                    onChange={(event) => onNuevoGastoChange("compraTipo", event.target.value)}
                  >
                    <option value="contado">Contado</option>
                    <option value="credito">Crédito</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="gastoProveedor">
                    Proveedor
                  </Label>
                  <Input
                    id="gastoProveedor"
                    value={nuevoGasto.proveedor}
                    onChange={(event) => onNuevoGastoChange("proveedor", event.target.value)}
                    placeholder="Nombre fiscal o comercial"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label className="form-label" for="gastoRuc">
                    RUC
                  </Label>
                  <Input
                    id="gastoRuc"
                    value={nuevoGasto.ruc}
                    onChange={(event) => onNuevoGastoChange("ruc", event.target.value)}
                    placeholder="1234567-8"
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label className="form-label" for="gastoFecha">
                    Fecha
                  </Label>
                  <Input
                    id="gastoFecha"
                    type="date"
                    value={nuevoGasto.fecha ?? ""}
                    onChange={(event) => onNuevoGastoChange("fecha", event.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="gastoNumero">
                    Comprobante
                  </Label>
                  <Input
                    id="gastoNumero"
                    value={nuevoGasto.numero}
                    onChange={(event) => onNuevoGastoChange("numero", event.target.value)}
                    placeholder="Factura, ticket o recibo"
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="gastoDescripcion">
                    Descripción
                  </Label>
                  <Input
                    id="gastoDescripcion"
                    value={nuevoGasto.descripcion}
                    onChange={(event) => onNuevoGastoChange("descripcion", event.target.value)}
                    placeholder="Ej: Compra de insumos de oficina"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label className="form-label" for="gastoMonto">
                    Monto total (Gs)
                  </Label>
                  <Input
                    id="gastoMonto"
                    type="number"
                    min="0"
                    value={nuevoGasto.monto}
                    onChange={(event) => onNuevoGastoChange("monto", Number(event.target.value))}
                  />
                </FormGroup>
              </Col>
              <Col md="8">
                <FormGroup>
                  <Label className="form-label" for="gastoNota">
                    Observaciones
                  </Label>
                  <Input
                    id="gastoNota"
                    type="textarea"
                    rows={2}
                    value={nuevoGasto.nota ?? ""}
                    onChange={(event) => onNuevoGastoChange("nota", event.target.value)}
                    placeholder="Detalle interno o indicaciones para tesorería."
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                <div>
                  <h6 className="mb-1">Ítems del comprobante</h6>
                  <small className="text-muted">
                    Desglosá los conceptos si necesitás calcular el IVA automáticamente.
                  </small>
                </div>
                <Button color="success" size="sm" type="button" onClick={onAgregarItem}>
                  + Agregar ítem
                </Button>
              </div>
              <div className="table-responsive">
                <Table className="align-middle table-nowrap">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 200 }}>Descripción</th>
                      <th style={{ minWidth: 120 }}>Código</th>
                      <th style={{ minWidth: 120 }}>Artículo</th>
                      <th style={{ width: 110 }}>Unidad</th>
                      <th style={{ width: 90 }}>Cant.</th>
                      <th style={{ width: 130 }}>Precio</th>
                      <th style={{ width: 130 }}>Monto</th>
                      <th style={{ width: 110 }}>IVA</th>
                      <th style={{ width: 70 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(nuevoGasto.articulos ?? []).map((item, index) => (
                      <tr key={`art-${index}`}>
                        <td>
                          <Input
                            value={item.descripcion}
                            onChange={(event) => onActualizarItem(index, "descripcion", event.target.value)}
                            placeholder="Descripción"
                            required
                          />
                        </td>
                        <td>
                          <Input
                            value={item.codigo ?? ""}
                            onChange={(event) => onActualizarItem(index, "codigo", event.target.value)}
                            placeholder="Código"
                          />
                        </td>
                        <td>
                          <Input
                            value={item.articulo ?? ""}
                            onChange={(event) => onActualizarItem(index, "articulo", event.target.value)}
                            placeholder="Artículo"
                          />
                        </td>
                        <td>
                          <Input
                            value={item.unidad ?? "UNI"}
                            onChange={(event) => onActualizarItem(index, "unidad", event.target.value)}
                            placeholder="UNI"
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            min="0"
                            value={item.cantidad}
                            onChange={(event) => onActualizarItem(index, "cantidad", Number(event.target.value))}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            min="0"
                            value={item.precioUnitario}
                            onChange={(event) =>
                              onActualizarItem(index, "precioUnitario", Number(event.target.value))
                            }
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            min="0"
                            value={item.monto}
                            onChange={(event) => onActualizarItem(index, "monto", Number(event.target.value))}
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            min="0"
                            value={item.iva}
                            onChange={(event) => onActualizarItem(index, "iva", Number(event.target.value))}
                          />
                        </td>
                        <td className="text-end">
                          <Button
                            color="link"
                            className="text-danger px-0"
                            onClick={() => onEliminarItem(index)}
                            disabled={(nuevoGasto.articulos ?? []).length === 1}
                          >
                            Quitar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mt-2 gap-2">
                <small className="text-muted">
                  Si no detallás ítems, se tomará el monto general del comprobante.
                </small>
                <Button color="link" type="button" className="p-0" onClick={onAgregarItem}>
                  + Otro ítem
                </Button>
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="justify-content-between flex-wrap gap-2">
            <Button color="secondary" type="button" onClick={toggleModal}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={!puedeGuardar}>
              Agregar gasto
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

export default GastosStep;
